import React from 'react';
import { Waypoint } from 'react-waypoint';
import { List as VirtualList } from 'react-window';

import { MemoPage } from './components/PdfPage';
import { useListScrollTracking } from './hooks/usePageScrollTracking';

interface PdfPreviewProps {
  selectedPage?: number;
  onPageChange?: (page: number) => void;
  pages?: number[];
}

const ROW_HEIGHT = 800;
const OVERSCAN = 5;

const PdfPreview: React.FC<PdfPreviewProps> = ({
  selectedPage,
  pages = [],
  onPageChange,
}) => {
  const pageCache = React.useRef<Map<number, JSX.Element>>(new Map());

  const { containerRef, isProgrammaticScrollRef, currentPageRef } =
    useListScrollTracking({
      onPageChange,
      selectedPage,
      totalPages: pages.length,
    });

  const handlePageEnter = (
    _args: Waypoint.CallbackArgs,
    pageNumber: number
  ) => {
    if (pageNumber === currentPageRef.current) return;
    if (isProgrammaticScrollRef.current) return;
    onPageChange?.(pageNumber);
  };

  return (
    <VirtualList
      rowCount={pages.length}
      rowHeight={ROW_HEIGHT}
      rowProps={{}}
      listRef={containerRef}
      rowComponent={({ index, style }) => {
        const pageNumber = pages[index];

        let pageEl = pageCache.current.get(pageNumber);

        if (!pageEl) {
          pageEl = (
            <MemoPage
              pageNumber={pageNumber}
              renderTextLayer={true}
              renderAnnotationLayer={false}
              scale={1}
              width={594}
            />
          );
          pageCache.current.set(pageNumber, pageEl);
        }

        return (
          <div style={style}>
            <Waypoint
              onEnter={(args) => handlePageEnter(args, pageNumber)}
              bottomOffset={'30%'}
              topOffset={'30%'}
            >
              <div>{pageEl}</div>
            </Waypoint>
          </div>
        );
      }}
      overscanCount={OVERSCAN}
    />
  );
};

export default React.memo(PdfPreview, (prev, next) => {
  return (
    JSON.stringify(prev.pages) === JSON.stringify(next.pages) &&
    prev.selectedPage === next.selectedPage
  );
});
